/**
 * Image Layer Renderer
 * Renders image layers with object-fit support and inline SVG rendering
 */

import React, { useMemo } from 'react';
import type { ImageLayer } from '@/types/layers';
import type { ResolvedPosition } from '@/lib/position/resolver';
import { applyColorOverrides } from '@/lib/svg/parser';

interface ImageLayerRendererProps {
  layer: ImageLayer;
  position: ResolvedPosition;
  isSelected?: boolean;
  isHovered?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function ImageLayerRenderer({
  layer,
  position,
  isSelected,
  isHovered,
  onClick,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
}: ImageLayerRendererProps) {
  const { content, transform, opacity, width, height } = layer;

  const resolvedWidth = position.width ?? (typeof width === 'number' ? width : 'auto');
  const resolvedHeight = position.height ?? (typeof height === 'number' ? height : 'auto');

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    left: position.x,
    top: position.y,
    width: resolvedWidth,
    height: resolvedHeight,
    opacity,
    transform: transform
      ? `rotate(${transform.rotation ?? 0}deg) scale(${transform.scaleX ?? 1}, ${transform.scaleY ?? 1})`
      : undefined,
    transformOrigin: transform
      ? `${(transform.originX ?? 0.5) * 100}% ${(transform.originY ?? 0.5) * 100}%`
      : undefined,
    overflow: 'hidden',
    borderRadius: content.borderRadius ?? 0,
    // Hover styling (selection handled by SelectionOverlay)
    outline: isHovered && !isSelected ? '1px dashed #3B82F6' : 'none',
    outlineOffset: 2,
    cursor: 'pointer',
    pointerEvents: layer.locked ? 'none' : 'auto',
  };

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: content.objectFit ?? 'cover',
    objectPosition: content.objectPosition ?? 'center',
    filter: content.filter,
    display: 'block',
  };

  // Process SVG content with color overrides
  const processedSvgContent = useMemo(() => {
    if (!content.isSvg || !content.svgContent) return null;
    return applyColorOverrides(content.svgContent, content.colorOverrides || {});
  }, [content.isSvg, content.svgContent, content.colorOverrides]);

  // Render inline SVG for SVG content
  if (content.isSvg && processedSvgContent) {
    return (
      <div
        style={containerStyle}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-layer-id={layer.id}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            filter: content.filter,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          dangerouslySetInnerHTML={{ __html: processedSvgContent }}
          className="[&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain"
        />
      </div>
    );
  }

  // Render regular image
  return (
    <div
      style={containerStyle}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-layer-id={layer.id}
    >
      <img
        src={content.src}
        alt={content.alt ?? ''}
        style={imageStyle}
        draggable={false}
      />
    </div>
  );
}
